import axios from 'axios';
import { API_URL } from '../../config/api';
import { utcTime } from '../users/model';
export interface UserSession {
  id: number; user_id: number; user_username: string | null; user_name: string | null;
  user_avatar: string | null; device_name: string | null; device_type: string | null;
  platform: string | null; location: string | null; ip_address: string | null;
  is_current: boolean; is_active: boolean; last_active: string | null; created_at: string | null;
}
export interface SessionStats {
  total_sessions: number; active_sessions: number;
  by_platform: { platform: string | null; count: number }[];
  by_device_type: { type: string | null; count: number }[];
}
export interface Filters { active: '' | 'active' | 'inactive'; platform: string; type: string; current: '' | 'yes' | 'no' }
export const defaults: Filters = { active: '', platform: '', type: '', current: '' };
export const platforms: Record<string, string> = { ios: 'iOS', android: 'Android', web: 'Web', macos: 'macOS', windows: 'Windows' };
export const deviceTypes: Record<string, string> = { phone: 'Telefon', tablet: 'Tablet', computer: 'Bilgisayar', browser: 'Tarayıcı' };
export const codeOf = (value: string | null) => value?.toLowerCase() || 'unknown';
const lookup = (map: Record<string, string>, value: string | null) => Object.prototype.hasOwnProperty.call(map, codeOf(value)) ? map[codeOf(value)] : value && value !== 'Unknown' ? value : 'Belirtilmemiş';
export const platformLabel = (value: string | null) => lookup(platforms, value);
export const typeLabel = (value: string | null) => lookup(deviceTypes, value);
export const toneOf = (value: string | null) => ['ios', 'android'].includes(codeOf(value)) ? codeOf(value) : ['web', 'macos', 'windows'].includes(codeOf(value)) ? 'desktop' : 'other';
export const nameOf = (item: UserSession) => item.user_name || (item.user_username !== 'Unknown' && item.user_username) || `Kullanıcı #${item.user_id}`;
export const usernameOf = (item: UserSession) => item.user_username && item.user_username !== 'Unknown' ? `@${item.user_username}` : 'Kullanıcı adı yok';
export const locationLabel = (value: string | null) => value && value !== 'Unknown' ? value : 'Belirtilmemiş';
export function matches(item: UserSession, search: string, filters: Filters) {
  const text = [item.id, item.user_id, item.user_username, item.user_name, item.device_name, typeLabel(item.device_type), platformLabel(item.platform), item.location, item.ip_address].join(' ').toLocaleLowerCase('tr-TR');
  return text.includes(search.trim().toLocaleLowerCase('tr-TR')) && (!filters.active || item.is_active === (filters.active === 'active')) &&
    (!filters.platform || codeOf(item.platform) === filters.platform) && (!filters.type || codeOf(item.device_type) === filters.type) && (!filters.current || item.is_current === (filters.current === 'yes'));
}
export type Sort = 'activity' | 'created' | 'name';
export function sortItems(items: UserSession[], sort: Sort) {
  const time = (item: UserSession) => { const raw = sort === 'created' ? item.created_at : item.last_active; const value = raw ? utcTime(raw) : NaN; return Number.isFinite(value) ? value : -Infinity; };
  return [...items].sort((a, b) => sort === 'name' ? nameOf(a).localeCompare(nameOf(b), 'tr-TR') || a.id - b.id : time(a) === time(b) ? b.id - a.id : time(a) > time(b) ? -1 : 1);
}
export async function fetchSessions() {
  const items: UserSession[] = [], seen = new Set<number>(); let skip = 0, total = 0;
  while (skip < 100000) {
    const { data } = await axios.get<{total: number; sessions: UserSession[]}>(`${API_URL}/admin/sessions`, { params: { skip, limit: 1000 } });
    if (!Array.isArray(data?.sessions) || !Number.isInteger(data.total) || data.total < 0) throw new Error('Invalid session list');
    total = data.total;
    if (!data.sessions.length && skip < total) throw new Error('Incomplete session list');
    for (const item of data.sessions) if (!seen.has(item.id)) { items.push(item); seen.add(item.id); }
    skip += data.sessions.length;
    if (skip >= total || !data.sessions.length) break;
  }
  return { items, total, partial: items.length < total };
}
export function requestError(error: unknown) {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    if (status === 401 || status === 403) return 'Bu işlem için yetkiniz yok veya oturumunuz sona erdi.';
    if (status === 404) return 'Oturum kaydı artık bulunmuyor. Listeyi yenileyin.';
    if (status === 429) return 'Çok fazla istek gönderildi. Biraz sonra yeniden deneyin.';
  }
  return 'İşlem tamamlanamadı. Yeniden deneyebilirsiniz.';
}
